import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { DialogsProvider } from "@/hooks/useDialogs";
import { ToastProvider } from "@/components/ui/Toast";
import App from "@/App";
import { PortalApp } from "@/portal/PortalApp";
import { PortalAuthProvider } from "@/portal/PortalAuthContext";
import "@/design-system/globals.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      // 5 minutes — most staff data tolerates minutes of staleness, and the
      // old 30s default made every page remount refetch its whole payload.
      // Genuinely live surfaces (badges, work queues, staging queues) opt
      // into a shorter per-query staleTime / refetchInterval at the call
      // site — keep that list in sync with the perf PR description.
      staleTime: 5 * 60 * 1000,
    },
  },
});

// External Intake & Collection v1.0 PR 4 — the external-party portal is a
// separate route tree under /portal/*. We branch at the top of the SPA so
// the staff <AuthProvider> (which calls /api/auth/me on mount) never fires
// for a recipient — keeping the two surfaces fully isolated.
// The public prequalification funnel is served under both /portal/prequalify
// (legacy) and the top-level /prequalify (native) + /prequalify/{slug}
// (white-label partner) routes — all part of the isolated portal bundle.
const isPortal =
  window.location.pathname.startsWith("/portal") ||
  window.location.pathname.startsWith("/prequalify");

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        {isPortal ? (
          <PortalAuthProvider>
            <ToastProvider>
              <PortalApp />
            </ToastProvider>
          </PortalAuthProvider>
        ) : (
          <AuthProvider>
            <DialogsProvider>
              <ToastProvider>
                <App />
              </ToastProvider>
            </DialogsProvider>
          </AuthProvider>
        )}
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>
);
